import React, { createRef, useEffect, useRef, useState } from 'react';
import {
  ConfirmationModal,
  Modal,
  Button,
  Notification,
  List,
  CustomIcon,
  OnlyInDesktop,
} from '@components/shared';
import { useHistory } from 'react-router-dom';
import { InView } from 'react-intersection-observer';
import { TourbookListing, Tourbook } from '@root/types';
import { SortableList, SortableListItem } from '@components/shared/sortable';
import { connect, ConnectedProps } from 'react-redux';
import routes from '@root/routes';
import tourbooksActions from '@store/actions/tourbooks';
import actions from '@store/actions/tourbookPage';
import { useTranslation, Trans } from 'react-i18next';
import classnames from 'classnames';
import darkContentCss from '@styles/darkContentSection.module.less';
import emailHref from '@shared/emailHref';
import useHash from '@shared/useHash';
import useSticky from '@shared/useSticky';
import contentWidth from '@styles/contentWidth.module.less';
import { CSSTransition } from 'react-transition-group';
import useDownloadTourbook from '@shared/useDownloadTourbook';
import tourbooksStyles from '../Tourbooks/Tourbooks.module.less';
import Layout from '../Layout';
import tourbookStyles from './Tourbook.module.less';
import TourbookMap from './TourbookMap';
import TourbookEmptyState from './TourbookEmptyState';
import TourbookHeader from './TourbookHeader';
import TourbookControls from './TourbookControls';
import TourbookListingCard from './TourbookListingCard';
import ReorderListingCard from './ReorderListingCard';

type Props = {
  tourbook: Tourbook;
  fetchTourbook: () => void;
  readOnly: boolean;
  tourbookDownload: () => void;
  tourbookRetryDownload: () => void;
};

const mapDispatch = {
  deleteTourbook: (tourbook: Tourbook, callbacks: { onSuccess: () => void; onError: () => void }) =>
    tourbooksActions.deleteTourbook(tourbook, callbacks),
  removeListingInTourbook: (listing: TourbookListing | null, tourbookId, onSuccessCallback) =>
    tourbooksActions.deleteTourbookListing({
      onSuccess: () => {
        onSuccessCallback(listing);
      },
      tourbookId,
      listingId: listing?.id || '',
      external: listing?.external || false,
    }),
  beginReorderingTourbookListings: actions.beginReorderingTourbookListings,
  endReorderingTourbookListings: actions.endReorderingTourbookListings,
  reorderedTourbookListing: actions.reorderedTourbookListing,
  openMap: actions.openTourbookMap,
  closeMap: actions.closeTourbookMap,
  tourbookDownload: () => actions.tourbookDownload(),
  tourbookRetryDownload: () => actions.tourbookRetryDownload(),
};

const connector = connect(null, mapDispatch);
type ReduxProps = ConnectedProps<typeof connector>;

const defaultModalState = {
  edit: false,
  delete: false,
  removeListing: false,
};

export const TourbookLayout = ({
  tourbook,
  removeListingInTourbook,
  fetchTourbook,
  deleteTourbook,
  readOnly,
  beginReorderingTourbookListings,
  endReorderingTourbookListings,
  reorderedTourbookListing,
  openMap,
  closeMap,
  tourbookDownload,
  tourbookRetryDownload,
}: Props & ReduxProps) => {
  const history = useHistory();
  const hash = useHash();
  const newestRef = useRef<HTMLDivElement | null>(null);
  const cardListRef = useRef<HTMLUListElement | null>(null);
  const { t } = useTranslation('tourbook');
  const [listingToRemove, setListingToRemove] = useState<TourbookListing | null>(null);
  const [hoveredListing, setHoveredListing] = useState<TourbookListing | null>(null);
  const [loadingPowerpoint, setLoadingPowerpoint] = useState(false);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [maxMapHeight, setMaxMapHeight] = useState<number | undefined>(undefined);

  const [modalState, setModalState] = useState<{
    edit: boolean;
    delete: boolean;
    removeListing: boolean;
  }>(defaultModalState);

  const downloadTourbook = useDownloadTourbook();

  const onRetryDownloadClick = () => {
    tourbookRetryDownload();
    onDownload();
  };

  const onDownload = async () => {
    downloadTourbook({
      tourbookId: tourbook.id,
      onStart: () => setLoadingPowerpoint(true),
      onFinish: () => {
        setLoadingPowerpoint(false);
        tourbookDownload();
      },
      onFailure: () => {
        setLoadingPowerpoint(false);
        const href = emailHref('support@vts.com', {
          subject: t('notification.downloadError.supportEmail.subject'),
          body: t('notification.downloadError.supportEmail.body', {
            tourbookUrl: window.location.href,
          }),
        });
        Notification.error({
          title: t('notification.downloadError.notificationTitle'),
          text: (
            <>
              {/* eslint-disable react/jsx-no-literals */}
              <Trans t={t} i18nKey="notification.downloadError.notificationText">
                Please retry your download, or contact&nbsp;
                <a href={href}>support@vts.com</a>
                &nbsp;if the issue persists.
              </Trans>
              {/* eslint-enable react/jsx-no-literals */}
              <Button
                type="primary"
                className={tourbookStyles.notificationRetryButton}
                onClick={onRetryDownloadClick}
              >
                {t('notification.downloadError.retryButton')}
              </Button>
            </>
          ),
        });
      },
    });
  };

  const onEditClick = () => {
    setModalState({ ...defaultModalState, edit: true });
  };

  const onDeleteClick = () => {
    setModalState({ ...defaultModalState, delete: true });
  };

  const onCloseModal = () => {
    setModalState(defaultModalState);
  };

  const handleDelete = () => {
    deleteTourbook(tourbook, {
      onSuccess: () => {
        Notification.info({
          title: t('notification.deleteSuccess'),
          placement: 'bottomLeft',
          duration: 3.5,
        });

        history.push(routes.tourbooks);
      },
      onError: () =>
        Notification.info({
          title: t('notification.deleteError'),
          placement: 'bottomLeft',
          duration: 3.5,
        }),
    });
    onCloseModal();
  };

  const onRemoveListingClick = listing => {
    setListingToRemove(listing);
    setModalState({ ...defaultModalState, removeListing: true });
  };

  const fullListingName = (listing: TourbookListing | null): string =>
    listing?.name
      ? `${listing?.building?.primaryName}, ${listing?.name}`
      : `${listing?.building?.primaryName}`;

  const removeListingSuccessCallback = (listing: TourbookListing) => {
    Notification.info({
      title: t('notification.deleteListingSuccess.title'),
      text: t('notification.deleteListingSuccess.text', { listingName: fullListingName(listing) }),
      placement: 'bottomLeft',
    });
  };

  const onRemoveListingConfirmClick = async () => {
    await removeListingInTourbook(listingToRemove, tourbook.id, removeListingSuccessCallback);
    fetchTourbook();
    onCloseModal();
  };

  const tourbookHeaderRef = createRef<HTMLDivElement | null>();
  const tourbookHeaderIntersection = useSticky(tourbookHeaderRef);
  const { intersectionRatio } = tourbookHeaderIntersection;
  const shouldCollapseHeader = intersectionRatio === undefined ? false : intersectionRatio! < 0.25;

  const ensureMapClosed = () => {
    if (isMapOpen) {
      setIsMapOpen(false);
      closeMap();
    }
  };

  const onMapCloseClick = () => {
    ensureMapClosed();
  };

  const onMapOpenClick = () => {
    setIsMapOpen(true);
    openMap();
  };

  const [isReordering, setIsReordering] = React.useState(false);
  const startReordering = () => {
    ensureMapClosed();
    setIsReordering(true);
    beginReorderingTourbookListings();
  };
  const stopReordering = () => {
    setIsReordering(false);
    endReorderingTourbookListings();
  };

  useEffect(() => {
    if (hash !== '#newest') return;
    const { current } = newestRef;
    if (!current) return;
    window.scrollTo({ top: current.offsetTop - 50, behavior: 'smooth' });
  }, [hash]);

  useEffect(() => {
    const handler = () => {
      if (cardListRef.current) setMaxMapHeight(cardListRef.current.clientHeight);
    };

    handler();

    window.visualViewport?.addEventListener('resize', handler);
    return () => {
      window.visualViewport?.removeEventListener('resize', handler);
    };
  }, []);

  useEffect(() => {
    setMaxMapHeight(cardListRef.current?.clientHeight);
  }, [cardListRef.current]);

  return (
    <Layout
      activePage="tourbookShow"
      containerClassName={classnames(
        tourbookStyles.tourbookShow,
        contentWidth.contentContainer,
        contentWidth.contentPadding,
        darkContentCss.darkContentSection,
      )}
    >
      <CSSTransition
        in={isMapOpen}
        timeout={1000}
        classNames={{
          enter: tourbookStyles.mobileMapEnter,
          enterActive: tourbookStyles.mobileMapEnterActive,
          exit: tourbookStyles.mobileMapExit,
          exitActive: tourbookStyles.mobileMapExitActive,
        }}
        unmountOnExit
      >
        <div
          className={classnames(tourbookStyles.mobileMapContainer)}
          data-testid="mobile-tourbook-map"
        >
          <Button
            type="primary"
            size="small"
            onClick={onMapCloseClick}
            className={tourbookStyles.mobileMapBackButton}
            data-testid="mapClose"
          >
            <CustomIcon type="close" />
          </Button>
          <TourbookMap
            className={tourbookStyles.mobileMap}
            tourbook={tourbook}
            hoveredListing={hoveredListing}
          />
        </div>
      </CSSTransition>
      {!isMapOpen && (
        <>
          <div className={tourbookStyles.mobileFooterButtonContainer}>
            <Button
              size="small"
              onClick={onMapOpenClick}
              className={tourbookStyles.mobileFooterButton}
            >
              <CustomIcon type="pin-drop" />
              <span className={tourbookStyles.viewAll}>{t('viewMap')}</span>
            </Button>
          </div>
        </>
      )}
      <TourbookControls
        tourbook={tourbook}
        onDeleteClick={onDeleteClick}
        onDownload={onDownload}
        onEditClick={onEditClick}
        readOnly={readOnly}
        isCollapsed={shouldCollapseHeader}
        isLoadingDownload={loadingPowerpoint}
      />
      <TourbookHeader
        ref={tourbookHeaderRef}
        tourbook={tourbook}
        onEditClick={onEditClick}
        isModalOpen={modalState.edit}
        onCloseModal={onCloseModal}
        readOnly={readOnly}
        hidden={false}
      />
      <Modal
        isOpen={modalState.removeListing}
        closeModal={onCloseModal}
        title={t('removeListingModal.title')}
      >
        <div className={tourbooksStyles.modal}>
          {t('removeListingModal.confirmationText', {
            listingName: fullListingName(listingToRemove),
          })}
          <div className={tourbooksStyles.buttons}>
            <Button type="secondary" size="large" onClick={onCloseModal}>
              {t('common:cancel')}
            </Button>
            <Button type="primary" size="large" onClick={onRemoveListingConfirmClick}>
              {t('common:remove')}
            </Button>
          </div>
        </div>
      </Modal>
      <ConfirmationModal
        title={t('deleteModal.title')}
        confirmationText={t('deleteModal.confirmationText')}
        isOpen={modalState.delete}
        cancelButtonText={t('common:cancel')}
        confirmButtonText={t('common:delete')}
        onClose={onCloseModal}
        onConfirm={handleDelete}
      />
      {tourbook.listings.length ? (
        <>
          {!readOnly ? (
            <div className={classnames(tourbookStyles.orderButtonContainer)}>
              {isReordering ? (
                <Button type="tertiary" onClick={stopReordering}>
                  {t('common:done')}
                </Button>
              ) : (
                <Button icon="filters" type="tertiary" onClick={startReordering}>
                  {t('reorder')}
                </Button>
              )}
            </div>
          ) : null}
          <div
            className={classnames(
              tourbookStyles.cardListAndMapContainer,
              readOnly && tourbookStyles.readOnly,
              contentWidth.contentContainer,
            )}
          >
            {isReordering ? (
              <section data-testid="reorderListingsSection" className={tourbookStyles.listings}>
                <SortableList
                  onDrop={tourbooksActions.reorderTourbookListings(tourbook.id)}
                  onSort={() => {
                    fetchTourbook();
                    reorderedTourbookListing();
                  }}
                  className={tourbookStyles.cardList}
                >
                  {tourbook.listings.map((listing, i) => (
                    <SortableListItem
                      index={i}
                      sortableId={listing.sortableId}
                      key={listing.id}
                      draggingClassName={tourbookStyles.isDragging}
                    >
                      <ReorderListingCard
                        listing={listing}
                        onHover={() => setHoveredListing(listing)}
                        onHoverExit={() => setHoveredListing(null)}
                      />
                    </SortableListItem>
                  ))}
                </SortableList>
              </section>
            ) : (
              <section data-testid="showListingsSection" className={tourbookStyles.listings}>
                <List<TourbookListing>
                  ref={cardListRef}
                  className={tourbookStyles.cardList}
                  items={tourbook.listings}
                  renderItem={listing => (
                    <InView
                      trackVisibility
                      threshold={0.5}
                      delay={100}
                      onChange={inView => inView && setHoveredListing(listing)}
                    >
                      <div ref={newestRef}></div>
                      <TourbookListingCard
                        tourbookId={tourbook.id}
                        listing={listing}
                        readOnly={readOnly}
                        ownerId={tourbook.owner.id}
                        handleOnRemoveListingClick={() => onRemoveListingClick(listing)}
                        onHover={() => setHoveredListing(listing)}
                        onHoverExit={() => setHoveredListing(null)}
                      />
                    </InView>
                  )}
                />
              </section>
            )}
            <OnlyInDesktop>
              <TourbookMap
                className={classnames(
                  tourbookStyles.mapContainer,
                  shouldCollapseHeader && tourbookStyles.belowHeader,
                )}
                tourbook={tourbook}
                hoveredListing={hoveredListing}
                maxHeight={maxMapHeight}
              />
            </OnlyInDesktop>
          </div>
        </>
      ) : (
        <TourbookEmptyState />
      )}
    </Layout>
  );
};

export default connector(TourbookLayout);
